import { Component, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import moment from 'moment';
import { Subscription } from 'rxjs';

import { TranslatePipe } from '../../filters/translate.pipe';
import { LowerCasePipe } from '../../filters/lower-case.pipe';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { ListBanklinksComponent } from '../../components/list-banklinks/list-banklinks.component';
import { CardServiceComponent } from '../../components/card-service/card-service.component';
import { ItemNotificationComponent } from '../../components/item-notification/item-notification.component';
import { PanelDirective } from '../../directives/panel.directive';
import RoutingService from '../../services/routing.service';
import ServiceRestServices from '../../services/service-rest.service';
import ChatRestServices from '../../services/chat-rest.service';
import AngularFilterHelperService from '../../services/angular-filter-helper.service';
import Config from '../../utils/config.service';
import Session from '../../utils/session.service';
import UserService from '../../services/user.service';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import AngularHelperService from '../../services/angular-helper.service';
import AngularBroadcastHelperService from '../../services/angular-broadcast-helper.service';
import UrlUtil from '../../utils/url-util.service';

declare const Stripe: any;

@Component({
	standalone: true,
	selector: 'booking-offer-checkout',
	templateUrl: './booking-offer-checkout.component.html',
	imports: [
		CommonModule,
		FormsModule,
		RouterLink,
		TranslatePipe,
		LowerCasePipe,
		LoaderOverlayComponent,
		ListBanklinksComponent,
		CardServiceComponent,
		ItemNotificationComponent,
		PanelDirective,
		ReactiveFormsModule
	],
	styles: [`
		.booking-offer-page-loader {
			min-height: 220px;
			position: relative;
		}

		.booking-offer-payment-area {
			min-height: 106px;
			position: relative;
		}

		.booking-offer-payment-label {
			margin-bottom: 10px;
		}

		.booking-offer-stripe-form {
			margin-top: 8px;
		}

		.booking-offer-card-element {
			background: #fff;
			border: 1px solid rgba(50, 50, 93, .18);
			border-radius: 4px;
			box-sizing: border-box;
			min-height: 44px;
			padding: 12px;
			width: 100%;
		}

		.booking-offer-payment-error {
			color: #ff0000;
			font-size: 13px;
			line-height: 17px;
			margin-top: 8px;
		}
	`]
})
export default class BookingOfferCheckoutView implements OnInit, OnDestroy {
	private routingService: RoutingService = inject(RoutingService);
	private serviceRestServices: ServiceRestServices = inject(ServiceRestServices);
	private chatRestServices: ChatRestServices = inject(ChatRestServices);
	private angularFilterHelperService: AngularFilterHelperService = inject(AngularFilterHelperService);
	private config: Config = inject(Config);
	private session: Session = inject(Session);
	private userService: UserService = inject(UserService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private angularHelperService: AngularHelperService = inject(AngularHelperService);
	private angularBroadcastHelperService: AngularBroadcastHelperService = inject(AngularBroadcastHelperService);
	private urlUtil: UrlUtil = inject(UrlUtil);
	private ngZone: NgZone = inject(NgZone);

	vm: any = this;
	walletSubscription: Subscription | undefined;
	stripeInitialized = false;
	mainForm: FormGroup;

	constructor(private fb: FormBuilder) {
		this.mainForm = this.fb.group({
			termsCheckbox: [false, Validators.requiredTrue],
			ppCheckbox: [false, Validators.requiredTrue],
			dataProcessingCheckbox: [false],
			dataStorageTimeCheckbox: [false],
			dataSharingCheckbox: [false],
		});
	}

	ngOnInit() {
		this.routingService.validateAccess('booking').then(() => {
			this.vm.user = this.session.getSession().user;
			this.vm.isSiffi = this.vm.user?.origin == 'siffi.com';
			this.vm.isMinudoc = this.vm.user?.origin == 'minudoc.ee';
			this.vm.bookingOfferId = this.angularRoutingHelperService.getCurrentRouteParam('bookingOfferId');
			this.vm.offerPaymentMethod = 'stripe';
			this.vm.backUrl = ['/chats'];
			this.vm.isOpen = { service: true, overview: true, consents: true };
			this.vm.walletProcessed = false;
			this.vm.creditCardDataIncomplete = true;
			this.vm.gdprDisclaimerTranslated = this.angularFilterHelperService.translate('Modal.Content.Consents_Gdpr_Disclaimer');
			this.loadBookingOffer();
			this.walletSubscription = this.angularBroadcastHelperService.onEvent(this, 'walletChanged', (event, newWallet) => {
				this.handleWalletChange(newWallet);
			});
			this.userService.getWallet();
		});
	}

	ngOnDestroy() {
		if (this.walletSubscription) {
			this.walletSubscription.unsubscribe();
		}
	}

	loadBookingOffer() {
		this.vm.loading = true;
		this.vm.loadError = false;
		this.serviceRestServices.getBookingOffer({ bookingOfferId: this.vm.bookingOfferId }, (bookingOffer) => {
			if (!bookingOffer?.service?.id) {
				console.error('Booking offer service is missing', bookingOffer);
				this.handleBookingOfferLoadError();
				return;
			}
			this.serviceRestServices.getServiceById({ id: bookingOffer.service.id }, (service) => {
				this.initializeBookingOffer(bookingOffer, service);
			}, (error) => {
				console.error(error);
				this.initializeBookingOffer(bookingOffer, bookingOffer.service);
			});
		}, (error) => {
			console.error(error);
			this.handleBookingOfferLoadError();
		});
	}

	initializeBookingOffer(bookingOffer, service) {
		this.vm.booking = this.prepareBookingOffer(bookingOffer, service);
		this.getConsents();
		this.vm.loading = false;
		this.vm.loadError = false;
		this.vm.initialized = true;
		this.vm.walletProcessed = this.vm.walletProcessed || false;
		this.refreshPaymentState();
	}

	handleBookingOfferLoadError() {
		this.vm.loading = false;
		this.vm.loadError = true;
		this.vm.initialized = false;
	}

	prepareBookingOffer(bookingOffer, service) {
		const offerService = bookingOffer.service || {};
		bookingOffer.service = Object.assign({}, service || offerService);
		bookingOffer.service.id = offerService.id || bookingOffer.service.id;
		bookingOffer.service.scheduledStart = bookingOffer.scheduledStart;
		bookingOffer.service.pricing = Object.assign({}, bookingOffer.service.pricing || {}, {
			price: bookingOffer.servicePrice,
			currency: bookingOffer.serviceCurrency,
			timePerPrice: bookingOffer.serviceTimePerPrice
		});
		bookingOffer.totalCost = bookingOffer.customerRemainingAmount || 0;
		bookingOffer.price = bookingOffer.totalCost;
		bookingOffer.currency = bookingOffer.serviceCurrency;
		bookingOffer.date = moment(bookingOffer.scheduledStart).locale(this.vm.user.deviceLanguage.slice(0, 2)).format('dddd, D MMM YYYY');
		bookingOffer.start = moment(bookingOffer.scheduledStart).format('HH:mm');
		bookingOffer.end = moment(bookingOffer.scheduledEnd).format('HH:mm');
		bookingOffer.duration = moment.duration(moment(bookingOffer.scheduledEnd).diff(moment(bookingOffer.scheduledStart))).asMinutes();
		bookingOffer.backUrl = ['/chats'];
		bookingOffer.privateWalletBalance = this.vm.balance?.balance || 0;
		bookingOffer.employerCoveredAmount = bookingOffer.employerCoveredAmount || 0;
		return bookingOffer;
	}

	handleWalletChange(newWallet) {
		if (this.angularHelperService.equals(newWallet, this.vm.balance)) return;
		this.vm.balance = newWallet || {};
		this.vm.walletProcessed = true;
		this.refreshPaymentState();
	}

	refreshPaymentState() {
		if (!this.vm.booking) {
			return;
		}
		this.vm.booking.privateWalletBalance = this.vm.balance?.balance || 0;
		this.vm.leftToPay = Math.max(0, (this.vm.booking.totalCost || 0) - this.vm.booking.privateWalletBalance);
		if (this.vm.walletProcessed && this.vm.leftToPay > 0 && this.isStripeVisible()) {
			this.handlePaymentMethodsInit();
		}
	}

	isExtraConsentsRequired() {
		return this.vm.user.origin == 'siffi.com'
			|| (this.vm.booking && this.vm.booking.service && !this.vm.booking.service.minudocExtraConsentsDisabled);
	}

	getConsents() {
		var extendedConsentsRequired = this.isExtraConsentsRequired();

		if (extendedConsentsRequired) {
			this.mainForm.get('dataProcessingCheckbox')?.setValidators(Validators.requiredTrue);
			this.mainForm.get('dataStorageTimeCheckbox')?.setValidators(Validators.requiredTrue);
			this.mainForm.get('dataSharingCheckbox')?.setValidators(Validators.requiredTrue);
			this.vm.dataProcessingAcceptedVisible = true;
			this.vm.dataStorageTimeAcceptedVisible = true;
			this.vm.dataSharingAcceptedVisible = true;
			this.vm.gdprDisclaimerVisible = false;
		} else {
			this.mainForm.get('dataProcessingCheckbox')?.clearValidators();
			this.mainForm.get('dataStorageTimeCheckbox')?.clearValidators();
			this.mainForm.get('dataSharingCheckbox')?.clearValidators();
			this.vm.dataProcessingAcceptedVisible = false;
			this.vm.dataStorageTimeAcceptedVisible = false;
			this.vm.dataSharingAcceptedVisible = false;
			this.vm.gdprDisclaimerVisible = true;
		}

		this.mainForm.get('dataProcessingCheckbox')?.updateValueAndValidity();
		this.mainForm.get('dataStorageTimeCheckbox')?.updateValueAndValidity();
		this.mainForm.get('dataSharingCheckbox')?.updateValueAndValidity();

		if (!this.vm.booking.consents) {
			this.vm.booking.consents = {};
			this.vm.booking.consents.dataProcessingAccepted = this.vm.dataProcessingAcceptedVisible ? false : null;
			this.vm.booking.consents.dataStorageTimeAccepted = this.vm.dataStorageTimeAcceptedVisible ? false : null;
			this.vm.booking.consents.dataSharingAccepted = this.vm.dataSharingAcceptedVisible ? false : null;
		} else {
			this.vm.booking.consents.terms = !!this.vm.booking.consents.id;
		}

		this.vm.links = {};
		if (this.vm.user.origin == 'minudoc.ee') {
			this.vm.links.tos = this.urlUtil.createPublicUrl({
				'et_EE': '/tto/kasutustingimused',
				'en_GB': '/hsp/terms-of-service',
				'ru_RU': '/pmu/uslovija-ispolzovanija',
				'fi_FI': '/tpt/kayttoehdot'
			});
			this.vm.links.pp = this.urlUtil.createPublicUrl({
				'et_EE': '/tto/privaatsuspoliis',
				'en_GB': '/hsp/privacy-policy',
				'ru_RU': '/pmu/politika-konfidencialnosti',
				'fi_FI': '/tpt/tietosuojakaytanto'
			});
		} else {
			var locale: any = (this.vm.user && this.vm.user.deviceLanguage) || 'en_GB';
			this.vm.links.tos = this.config.getSiffiPublicWebUrl(locale, '/hsp/terms-of-service');
			this.vm.links.pp = this.config.getSiffiPublicWebUrl(locale, '/hsp/privacy-policy');
		}
		this.vm.termsConsent = this.angularHelperService.trustAsHtml(
			this.angularFilterHelperService.translate('Modal.Content.AgreeTo')
				.replace('%s', '<a href="' + this.vm.links.tos +'" target="_blank">' + this.angularFilterHelperService.translate('Modal.Content.ToS') + '</a>')
		);
		this.vm.ppConsent = this.angularHelperService.trustAsHtml(
			this.angularFilterHelperService.translate('Modal.Content.HaveRead')
				.replace('%s', '<a href="' + this.vm.links.pp +'" target="_blank">' + this.angularFilterHelperService.translate('Modal.Content.PrivacyPolicy') + '</a>')
		);
	}

	validateConsents() {
		this.vm.submitted = true;
		if (!this.mainForm.valid) {
			this.mainForm.markAllAsTouched();
			this.vm.isOpen.consents = true;
			this.vm.isOpen.overview = true;
			return false;
		}
		return true;
	}

	getConsentsUpdated() {
		var extendedConsentsRequired = this.isExtraConsentsRequired();

		this.vm.booking.consents = {};
		this.vm.booking.consents.terms = this.mainForm.get('termsCheckbox').value && this.mainForm.get('ppCheckbox').value;
		if (extendedConsentsRequired) {
			this.vm.booking.consents.dataProcessingAccepted = this.mainForm.get('dataProcessingCheckbox').value;
			this.vm.booking.consents.dataStorageTimeAccepted = this.mainForm.get('dataStorageTimeCheckbox').value;
			this.vm.booking.consents.dataSharingAccepted = this.mainForm.get('dataSharingCheckbox').value;
		}
		return this.vm.booking.consents;
	}

	handleBookingOfferPaymentMethodChange() {
		if (!this.vm.isMinudoc) {
			this.vm.offerPaymentMethod = 'stripe';
			return;
		}
		if (this.vm.offerPaymentMethod === 'stripe') {
			this.handlePaymentMethodsInit();
		}
	}

	handlePaymentMethodsInit() {
		if (this.stripeInitialized || !this.vm.walletProcessed || !this.vm.booking || this.vm.leftToPay <= 0) {
			return;
		}
		if (this.isStripeVisible()) {
			this.handleStripeInit();
		}
	}

	isStripeVisible() {
		return this.vm.isSiffi || this.vm.offerPaymentMethod !== 'banklinks';
	}

	shouldShowPaymentMethodSelection() {
		return this.vm.isMinudoc && this.vm.leftToPay > 0;
	}

	shouldShowBanklinks() {
		return this.vm.leftToPay > 0 && !this.isStripeVisible() && this.vm.isMinudoc;
	}

	isPaymentDisabled() {
		if (this.vm.isProcessing || !this.vm.booking?.id) {
			return true;
		}
		if (this.shouldUseStripePayment()) {
			return this.vm.stripeLoading || this.vm.stripeLoadError || this.vm.creditCardDataIncomplete || !this.vm.payWithCreditCard;
		}
		return this.vm.banklinksLoading || this.vm.banklinksLoadError || !this.vm.banklink;
	}

	handleStripeInit() {
		this.stripeInitialized = true;
		this.vm.stripeLoading = true;
		this.vm.stripeLoadError = false;
		this.vm.creditCardDataIncomplete = true;
		this.loadScript('https://js.stripe.com/v3/', 'stripe-js')
			.then(() => {
				setTimeout(() => {
					try {
						const stripe: any = Stripe(this.config.getStripePublishedKey());
						const configParams: RequestInit = {
							method: 'POST',
							credentials: 'include',
							headers: {
								'Content-Type': 'application/json'
							},
							body: JSON.stringify({ id: this.vm.booking.id })
						};

						fetch(this.config.getRestUrl() + 'rest/payment/stripe/create/intent/booking/offer', configParams)
							.then((result) => {
								if (!result.ok) {
									throw result;
								}
								return result.json();
							})
							.then((data) => {
								const elements: any = stripe.elements();
								const style: any = {
									base: {
										color: '#212121',
										fontFamily: 'Titillium Web, sans-serif',
										fontSmoothing: 'antialiased',
										fontSize: '15px',
										'::placeholder': { color: '#212121' }
									},
									invalid: {
										fontFamily: 'Titillium Web, sans-serif',
										color: '#fa755a',
										iconColor: '#fa755a'
									}
								};
								const card: any = elements.create('card', { style: style });
								card.mount('#card-element');
								card.on('change', (event) => {
									this.ngZone.run(() => {
										const cardErrorEl: any = document.querySelector('#card-error');
										if (cardErrorEl) {
											cardErrorEl.textContent = event.error ? event.error.message : '';
										}
										this.vm.creditCardDataIncomplete = !event.complete;
									});
								});
								this.ngZone.run(() => {
									this.vm.stripePayment = data;
									this.vm.stripeLoading = false;
									this.vm.stripeLoadError = false;
									this.vm.payWithCreditCard = () => {
										if (!this.validateConsents() || this.vm.isProcessing || this.vm.creditCardDataIncomplete) {
											return;
										}
										this.vm.isProcessing = true;
										this.payWithCard(stripe, card, data.clientSecret);
									};
								});
							})
							.catch((error) => {
								this.handleStripeInitError(error);
							});
					} catch (error) {
						this.handleStripeInitError(error);
					}
				}, 500);
			})
			.catch((error) => {
				this.handleStripeInitError(error);
			});
	}

	handleStripeInitError(error) {
		console.error(error);
		this.ngZone.run(() => {
			this.stripeInitialized = false;
			this.vm.stripeLoading = false;
			this.vm.stripeLoadError = true;
			this.vm.payWithCreditCard = undefined;
			this.vm.creditCardDataIncomplete = true;
		});
	}

	payWithCard(stripe, card, clientSecret) {
		stripe.confirmCardPayment(clientSecret, { payment_method: { card: card } })
			.then((result) => {
				if (result.error) {
					this.vm.isProcessing = false;
					this.showStripeError(result.error.message);
				} else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
					this.orderComplete('COMPLETED');
				} else {
					this.orderComplete('ERROR');
				}
			});
	}

	orderComplete(status) {
		this.vm.stripePayment.status = status;
		this.vm.stripePayment.platform = this.vm.booking?.platform || this.config.getSettings().PLATFORM;
		const configParams: RequestInit = {
			method: 'PUT',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(this.vm.stripePayment)
		};
		fetch(this.config.getRestUrl() + 'rest/payment/stripe/complete', configParams)
			.then((result) => result.json())
			.then(() => {
				if (status === 'COMPLETED') {
					this.completeBookingAfterStripePayment();
				} else {
					this.stripeInitialized = false;
					this.vm.isProcessing = false;
					this.handleStripeInit();
				}
			})
			.catch((error) => {
				console.error(error);
				this.vm.isProcessing = false;
			});
	}

	completeBookingAfterStripePayment() {
		this.userService.clearWalletCache();
		this.vm.isProcessing = false;
		this.angularRoutingHelperService.setPath('/booking/checkout/success', false, true, { bookingOfferId: this.vm.booking.id });
	}

	handleBanklinkPayment() {
		if (!this.validateConsents() || !this.vm.banklink || !this.vm.booking?.id || this.vm.isProcessing) {
			return;
		}

		this.vm.isProcessing = true;
		const banklink: any = this.vm.banklink;
		const form: any = document.createElement('form');
		form.name = banklink.bankName;
		form.target = '_top';
		form.method = 'POST';
		form.action = banklink.serviceUrl;

		Object.keys(banklink.parameters || {}).forEach((param) => {
			const input: any = document.createElement('input');
			input.type = 'hidden';
			input.name = param;
			input.value = banklink.parameters[param];
			form.appendChild(input);
		});

		document.body.appendChild(form);
		form.submit();
		this.userService.clearWalletCache();
	}

	handlePayment() {
		if (!this.validateConsents()) {
			return;
		}
		if (this.vm.leftToPay <= 0) {
			this.acceptBookingOffer();
			return;
		}
		if (this.isPaymentDisabled()) {
			return;
		}
		if (this.shouldUseStripePayment()) {
			this.vm.payWithCreditCard();
		} else {
			this.handleBanklinkPayment();
		}
	}

	shouldUseStripePayment() {
		return this.vm.isSiffi || this.vm.offerPaymentMethod === 'stripe';
	}

	acceptBookingOffer() {
		if (!this.validateConsents() || !this.vm.booking?.id || this.vm.isProcessing) {
			return;
		}
		this.vm.isProcessing = true;
		this.chatRestServices.acceptBookingOffer({ bookingOfferId: this.vm.booking.id }, () => {
			this.userService.clearWalletCache();
			this.vm.isProcessing = false;
			this.angularRoutingHelperService.setPath('/booking/checkout/success', false, true, { bookingOfferId: this.vm.booking.id });
		}, (error) => {
			console.error(error);
			this.vm.isProcessing = false;
		});
	}

	getBookingOfferEmployerCoveredAmount() {
		return this.vm.booking?.employerCoveredAmount || 0;
	}

	getBookingOfferTotalCost() {
		return this.vm.booking?.totalCost || this.vm.booking?.price || 0;
	}

	showStripeError(errorMsgText) {
		const errorMsgEl: any = document.querySelector('#card-error');
		if (errorMsgEl) {
			errorMsgEl.textContent = errorMsgText;
			setTimeout(() => {
				errorMsgEl.textContent = '';
			}, 4000);
		}
	}

	loadScript(src, id) {
		if (!src) {
			return this.angularHelperService.getPromise().reject('No src specified');
		}
		if (document.getElementById(id)) {
			return this.angularHelperService.getPromise().resolve();
		}
		return new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.id = id;
			script.src = src;
			script.onload = resolve;
			script.onerror = reject;
			document.body.appendChild(script);
		});
	}
}
